import React, { FC } from 'react';
import EditorErrorIcon from '@atlaskit/icon/glyph/editor/error';
import EditorHintIcon from '@atlaskit/icon/glyph/editor/hint';
import EditorWarningIcon from '@atlaskit/icon/glyph/editor/warning';
import css from '@emotion/css';
import { token } from '@atlaskit/tokens';

import * as styles from '@/components/composed/slo-card/graph/fault-zone/log-styles';
import { colors, flex } from '@/styles';
import { formatError } from '@/components/composed/slo-card/graph/fault-zone/utils';
import { formatDateForDisplay } from '@/utils';
import { DateDisplayFormats } from '@/constants';
import { logItemStyles } from '@/components/composed/slo-card/graph/fault-zone/log-styles';

export interface SeverityLevel {
  type: string;
  icon: JSX.Element;
  severity: number;
}

export const LogItemSeverity: { [key: string]: SeverityLevel } = {
  LOG: {
    type: 'log',
    icon: <EditorHintIcon label="" primaryColor={token('color.background.neutral', colors.N500)} />,
    severity: 0,
  },
  WARNING: {
    type: 'warning',
    icon: (
      <EditorWarningIcon label="" primaryColor={token('color.background.warning', colors.Y500)} />
    ),
    severity: 1,
  },
  ERROR: {
    type: 'error',
    icon: <EditorErrorIcon label="" primaryColor={token('color.background.danger', colors.R500)} />,
    severity: 2,
  },
};

export interface LogItemProps {
  title?: string;
  message: string;
  timestamp?: Date;
  severity: SeverityLevel;
  docLink?: string;
}

const LogItem: FC<LogItemProps> = ({
  title,
  message,
  timestamp,
  severity = LogItemSeverity.LOG,
  docLink,
}) => (
  <li css={[styles.item]}>
    <div css={[flex]}>
      <div css={logItemStyles}>
        <div css={[flex]}>{severity.icon}</div>
      </div>
      <div
        css={css`
          flex-grow: 10;
        `}
      >
        {title && (
          <p>
            <b>{title}</b>
          </p>
        )}

        <p>{formatError(message)}</p>
        {docLink && (
          <p>
            <em>
              For more information about this warning, see the{' '}
              <a target="_blank" rel="noopener noreferrer" href={docLink}>
                docs
              </a>
              .
            </em>
          </p>
        )}

        {timestamp && (
          <time
            css={[styles.timestamp]}
            dateTime={(timestamp instanceof Date ? timestamp : new Date(timestamp)).toISOString()}
          >
            {formatDateForDisplay(timestamp, DateDisplayFormats.TIME_WITH_NUMERIC_DATE)}
          </time>
        )}
      </div>
    </div>
  </li>
);

export default LogItem;
